import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { OtherCaseListService } from 'src/app/core/service/grid-data/grid-data-other-cases.service';
import moment from 'moment';
import { TaskUtility } from 'src/app/task/task.utility';
import { CaseUtility } from '../case.utility';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { User } from 'src/app/user/model/user';
import { IntlService } from '@progress/kendo-angular-intl';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';

@Component({
  selector: 'app-case-list-detail',
  templateUrl: './case-list-detail.component.html',
  styleUrls: ['./case-list-detail.component.scss'],
  providers: [OtherCaseListService]
})
export class CaseListDetailComponent implements OnInit, OnDestroy
{
  @Input() public caseId = 0;
  @Input() public referralsOnly = false;
  @Input() public patientSummary = false;
  @Input() public ehrContext = false;

  @Output() public referralsOpenStateChanged: EventEmitter<boolean> = new EventEmitter();

  /**
   * Case list Kendo grid state.
   */
  public caseState: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [{ field: 'id', dir: 'desc' }],
    take: 10
  };

  public referralsEnabled = false;
  public currentMemberId: number;
  public pageSizes = [5, 10, 25, 50, 100];
  public caseFilters: Array<{ text: string; value: string }>;
  public selectedCaseFilter = 'OPEN';
  public caseGridView: GridDataResult;
  public caseGridData: any = {};
  public totalCases: number;
  public loadedOtherCases = false;
  public caseOtherCasesCollapsed = false;
  public caseReferralsCollapsed = false;
  public currentUserId: number;
  public isOperatorRole = false;
  public isCareCoordinatorOrProgramLead = false;

  private currentUser: User;
  private messagingSubscription: Subscription;

  constructor(
    private messengerService: MessengerService,
    private otherCaseListService: OtherCaseListService,
    private authenticationService: AuthenticationService,
    private eventListService: EventListService,
    private caseUtilityService: CaseUtility,
    private taskUtilityService: TaskUtility,
    private settingService: SettingService,
    private userService: UserService,
    public intl: IntlService)
  {
    this.caseState.take = 5;
    this.caseFilters = this.caseUtilityService.caseFilters;
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'reloadCaseListDetailComponent' || message.type === 'reloadReferrals')
        {
          // Let some delay to let the workflow status to update but not 100% garanteed
          setTimeout(() =>
          {
            this.loadOtherCases();
          }, 5000);
        }
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get otherCasesloading(): boolean
  {
    return this.otherCaseListService.loading;
  }

  @Input() public set memberId(memberId: number)
  {
    if (memberId !== undefined)
    {
      this.settingService.getCachedSessionSettings().subscribe({
        next: (sessionSetting) =>
        {
          this.referralsEnabled = (sessionSetting.referralsRoles !== '') ?
            this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.referralsRoles) : false;

          this.currentMemberId = memberId;
          this.caseState.skip = 0;
          this.loadOtherCases();
        }
      });
    }
  }

  public ngOnInit(): void
  {
    const user = this.authenticationService.currentUser;
    this.currentUserId = user ? user.id : 0;
    this.currentUser = user;
    this.isOperatorRole = user.role === 'ROLE_OPERATOR';
    this.isCareCoordinatorOrProgramLead = user.role === 'ROLE_CARE_COORDINATOR' || user.role === 'ROLE_PROGRAM_LEAD';
    this.caseFilterSelected(this.selectedCaseFilter, true);
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public caseReferralsCollapsing(e: boolean): void
  {
    this.caseReferralsCollapsed = e;
    this.referralsOpenStateChanged.emit(e);
  }

  /**
   * Reassign a Referral by searching on the same service category and excluding the current case referred service.
   * New Referrals are handled in the patient summary therefore we must navigate to the patient summary first.
   *
   * @param dataItem Current case dataItem object.
   */
  public reassignReferral(dataItem: any): void
  {
    // For EHR context, we don't need to navigate to the patient summary and therefore we can send event to
    // the searchForServicesToReferred directly.
    if (this.ehrContext)
    {
      this.messengerService.sendMessage(new MessageSubject('searchForServicesToReferred',
        {
          categories: [dataItem.eventReferral.serviceCategoryId],
          referralReassign: true,
          context: 'member',
          parentCaseId: dataItem.id,
          excludedServices: [dataItem.eventReferral.referredProgramId],
        }
      ));
    }
    else
    {
      this.messengerService.sendMessage(new MessageSubject('newReferralForCategory',
        {
          categoryId: dataItem.eventReferral.serviceCategoryId,
          caseId: dataItem.id,
          excludeReferredServiceId: dataItem.eventReferral.referredProgramId
        }));
    }
  }

  /**
   * Case filter has been selected.
   *
   * @param filter Filter: Open, Completed, All
   */
  public caseFilterSelected(filter: string, isOnInit = false): void
  {
    this.selectedCaseFilter = filter;
    const filterChanged: any = {};
    filterChanged.filter = filter;
    if (!isOnInit)
    {
      this.loadOtherCases();
    }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public caseDataStateChange(state: DataStateChangeEvent): void
  {
    this.caseState = state;
    this.loadOtherCases();
  }

  public isChannelAccessAllowed(dataItem: any): boolean
  {
    if (dataItem && dataItem.users && dataItem.users.length > 0)
    {
      return dataItem.users.indexOf(this.currentUserId) >= 0;
    }
    else
    {
      if (dataItem.assignee && this.currentUser.userName === dataItem.assignee)
      {
        return true;
      }
      else
      {
        return false;
      }
    }
  }

  public showDetailsOnlyIfCaseAccessible = (dataItem: any) =>
  {
    let allowed = dataItem !== undefined && dataItem.tasks && dataItem.tasks.length > 0;
    if (allowed)
    {
      if (this.isOperatorRole)
      {
        allowed = true;
      }
      else if (this.isCareCoordinatorOrProgramLead)
      {
        allowed = this.isChannelAccessAllowed(dataItem);
      }
    }
    return allowed;
  };

  private loadOtherCases(): void
  {
    this.loadedOtherCases = false;
    this.caseState.filter =
    {
      logic: 'and',
      filters: []
    };

    this.caseState.filter.filters.push({ field: 'memberId', operator: 'eq', value: this.currentMemberId });
    if (this.caseId && this.caseId > 0)
    {
      this.caseState.filter.filters.push({ field: 'id', operator: 'neq', value: this.caseId });
    }

    switch (this.selectedCaseFilter)
    {
      case 'OPEN':
        this.caseState.filter.filters.push({ field: 'status', operator: 'eq', value: 'Open' });
        break;
      case 'CLOSED':
        this.caseState.filter.filters.push({ field: 'status', operator: 'eq', value: 'Closed' });
        break;
    }

    const referralOnlyFilter = this.referralsEnabled && (this.caseId > 0 || this.patientSummary) ?
      `&referralsOnly=${this.referralsOnly}` : '';

    this.otherCaseListService.queryByState(this.caseState, (value: any) =>
    {
      // Preprocessing data before it's passed to the Kendo Grid.
      if (value !== null)
      {
        value.data.forEach(d =>
        {
          d.priorityBadgeClass = this.caseUtilityService.priorityBadgeClass(d.priority);
          d.priorityLetter = this.caseUtilityService.priorityLetter(d.priority);
          d.enteredOn = d.enteredOn != null ? new Date(d.enteredOn) : null;
          d.openedOn = d.openedOn != null ? new Date(d.openedOn) : null;
          if (d.eventReferral !== null && d.eventReferral.referredDate !== null)
          {
            d.eventReferral.referredDate = new Date(d.eventReferral.referredDate);
          }

          // Set task for each cases
          const now = new Date();
          d.tasks.forEach(task =>
          {
            task.openedOn = new Date(task.openedOn);
            if (task.dueDate != null)
            {
              task.dueDate = new Date(task.dueDate);
              const a = moment(Date.UTC(task.dueDate.getFullYear(), task.dueDate.getMonth(), task.dueDate.getDate()));
              const b = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
              task.daysLeft = a.diff(b, 'days');
            }
            task.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(task.priority);
            task.priority = this.taskUtilityService.priority(task.priority);
            task.eventTypeIcon = this.eventListService.getEventTypeIcon(task.eventType);
            task.eventTypeText = this.eventListService.getEventTypeText(task.eventType);
            task.encounterTypeText = this.eventListService.getEncounterTypeText(task.encounterType);
            task.inCaseDetail = true; // indicates the task-detail component is shown in the Case-detail component.
          });
        });
      }
      this.caseGridView = {
        data: (value) ? this.caseGridData = value.data : this.caseGridData = [],
        total: (value) ? this.totalCases = value.total : this.totalCases = 0
      };
      this.loadedOtherCases = true;
    }, referralOnlyFilter, null, null, 'get', null, `/${this.caseId}`);    // Passing additional base url /{caseId}
  }
}
